import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { TimelineLayoutDTO } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { addDays, format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { VisualizationRanksApi } from '@/lib/api/loop-visualization/visualization-ranks';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import { useTimelineHeaderVisibilityMutation } from '@/lib/mutations/loop-visualization/useTimelineLayoutMutations';
import { loopVisualizationQueryKeys } from '@/lib/query-keys/loop-visualizations';
import { useSystemProgress } from '../utils/useSystemProgress';
import { useDependencyContext } from './dependencies/DependencyContext';
import { DependencyLayer } from './dependencies/DependencyLayer';
import { TimelineRowAxisLabel } from './layout/TimelineRowAxisLabel';
import { TimelineRowHeaderResize } from './layout/TimelineRowHeaderResize';
import { TimelineDroppableRow } from './TimelineDroppableRow';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { TimelineItemDragOverlayContent } from './TimelineItemDragOverlay';
import {
  DEFAULT_ROW_HEADER_WIDTH,
  PARENT_HEADER_HEIGHT,
  useTimelineColumnDayWidth,
  VERTICAL_ROW_HEADER_WIDTH,
} from './useTimelineColumnDayWidth';
import {
  type TimelineRowType,
  type TimelineTableCellContext,
  useTimelineTableColumns,
} from './useTimelineTableColumns';

class CustomPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent: event }: { nativeEvent: PointerEvent }) => {
        return shouldHandleEvent(event.target as HTMLElement);
      },
    },
  ];
}

const DRAG_DROP_ALLOWED_FIELDS = ['team', 'estimationId', 'parentItem'];
const BASE_ROW_OVERSCAN = 5;
const HEADER_CELL_HEIGHT = 32;

function shouldHandleEvent(element: HTMLElement | null): boolean {
  let cur = element;
  while (cur) {
    if (cur.dataset?.noDnd) {
      return false;
    }
    cur = cur.parentElement;
  }
  return true;
}

export const TimelineTable = ({
  from,
  to,
  data,
  canEdit,
  isPublishedMode,
  onItemDrop,
  onItemResize,
  header1FieldId,
  header2FieldId,
  scrollContainerRef,
  layout,
  expandedRows,
  setExpandedRows,
  itemColorById,
  rowHeaderWidth = DEFAULT_ROW_HEADER_WIDTH,
  onRowHeaderWidthChange,
  expandedChildren,
  setExpandedChildren,
  setReorderHeadersModalState,
  onItemClick: onItemClickProp,
  viewId,
  visualizationId,
  itemRanks,
  isExportMode = false,
}: {
  from: string;
  to: string;
  data: TimelineRowType[];
  canEdit: boolean;
  isPublishedMode: boolean;
  onItemDrop?: (
    itemId: number,
    row: TimelineRowType,
    newStartDate: string,
    newEndDate: string,
    sourceRow?: TimelineRowType,
  ) => void;
  onItemResize: (itemId: number, newStartDate: string, newEndDate: string) => void;
  header1FieldId: string;
  header2FieldId: string;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  expandedRows: ExpandedState;
  setExpandedRows: (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => void;
  layout?: TimelineLayoutDTO;
  itemColorById?: Record<number, string>;
  rowHeaderWidth?: number;
  onRowHeaderWidthChange?: (width: number) => void;
  expandedChildren: { [key: number]: boolean };
  setExpandedChildren: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
  setReorderHeadersModalState: React.Dispatch<
    React.SetStateAction<{
      isReorderingMainHeader: boolean;
      parentHeaderId: string | null;
    } | null>
  >;
  onItemClick?: (itemId: number) => void;
  viewId: number;
  visualizationId: number;
  itemRanks?: Map<number, string>;
  isExportMode?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { hideHeaderMutation } = useTimelineHeaderVisibilityMutation(viewId, visualizationId);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const { setTimelineContainerRef } = useDependencyContext();
  const { openItemPanel } = useItemPanelNavigation();
  const [activeDragData, setActiveDragData] = useState<{
    color: string;
    name: string;
    itemId: number;
    duration: number;
    startDate: string;
    endDate: string;
    isCompact?: boolean;
    currentRow?: TimelineRowType;
  } | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [forbiddenRowId, setForbiddenRowId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
  );

  const hasVerticalSubHeaders = useMemo(() => {
    return (
      layout?.headerDirection === 'vertical' &&
      data.length > 0 &&
      data[0]?.mainHeaderLabel !== undefined
    );
  }, [layout?.headerDirection, data]);

  const isVertical = layout?.headerDirection === 'vertical';
  const rowHeaderColumnWidth =
    isVertical && !hasVerticalSubHeaders ? VERTICAL_ROW_HEADER_WIDTH : rowHeaderWidth;

  const dayWidth = useTimelineColumnDayWidth({
    scrollContainerRef,
    hasVerticalSubHeaders,
    from,
    to,
    rowHeaderWidth: rowHeaderColumnWidth,
    isExportMode,
  });

  const timelineContainerRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      timelineContainerRef.current = node;
      if (node) {
        setTimelineContainerRef(node);
      }
    },
    [setTimelineContainerRef],
  );

  const onItemClick = useCallback(
    (itemId: number) => {
      if (onItemClickProp) {
        onItemClickProp(itemId);
      } else {
        openItemPanel({ itemId });
      }
    },
    [onItemClickProp, openItemPanel],
  );

  const onHideHeader = useCallback(
    (headerId: string, isMainHeader: boolean, parentHeaderId?: string) => {
      const headerFieldId = isMainHeader ? header1FieldId : header2FieldId;
      if (!headerFieldId) return;
      hideHeaderMutation.mutate({
        headerFieldId,
        headerId,
        parentFieldHeaderId: !isMainHeader ? header1FieldId : null,
        parentHeaderId: parentHeaderId ?? null,
      });
    },
    [header1FieldId, header2FieldId, hideHeaderMutation],
  );

  const hasUpdatableHeader =
    DRAG_DROP_ALLOWED_FIELDS.includes(header1FieldId) ||
    (!!header2FieldId && DRAG_DROP_ALLOWED_FIELDS.includes(header2FieldId));
  const canDragBetweenHeaders = canEdit && hasUpdatableHeader;
  const canDragDates = canEdit;
  const canResize = canEdit;

  const isSystemProgress = layout?.progressFieldType === 'system';
  const itemIds = useMemo(() => {
    const collectIds = (rows: TimelineRowType[]): number[] =>
      rows.flatMap((row) => [
        ...(row.allItems ?? []).map((item) => item.id),
        ...collectIds(row.subRows ?? []),
      ]);
    return collectIds(data);
  }, [data]);
  const { progressMap: systemProgressMap } = useSystemProgress({
    itemIds,
    enabled: isSystemProgress,
  });

  const columns = useTimelineTableColumns({
    from,
    to,
    canEdit,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    isPublishedMode,
    isVertical,
    hasVerticalSubHeaders,
    hasSubHeaders: Boolean(layout?.header2),
    showTodayLine: layout?.showTodayLine ?? false,
    titleSize: layout?.titleSize ?? 'medium',
    progressFieldId: isSystemProgress ? null : layout?.progressFieldId || null,
    systemProgressMap: isSystemProgress ? systemProgressMap : null,
    dayWidth,
    expandedChildren,
    showChildItems: layout?.showChildItems ?? false,
    setExpandedChildren,
    onResizeEnd: onItemResize,
    itemColorById,
    isCompact: layout?.theme === 'compact',
    rowHeaderWidth,
    onItemClick,
    setReorderHeadersModalState,
    onHideHeader: canEdit && !isPublishedMode ? onHideHeader : undefined,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded: expandedRows,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    onExpandedChange: setExpandedRows,
    getSubRows: (row) => row.subRows,
    getRowId: (row) => row.id,
  });

  const tableRows = table.getRowModel().rows;
  const numFixedColumns = hasVerticalSubHeaders ? 2 : 1;

  const isDropAllowed = useCallback(
    (sourceRow: TimelineRowType | undefined, targetRowId: string | undefined): boolean => {
      if (!targetRowId) return false;
      const targetRow = tableRows.find((r) => r.id === targetRowId);
      if (!targetRow) return false;

      if (sourceRow && targetRow.original.id === sourceRow.id) {
        return canDragDates;
      }

      if (!canDragBetweenHeaders) return false;

      const isHeader1Updatable = DRAG_DROP_ALLOWED_FIELDS.includes(header1FieldId);
      const isHeader2Updatable =
        !!header2FieldId && DRAG_DROP_ALLOWED_FIELDS.includes(header2FieldId);
      const hasHeader2 = !!header2FieldId;

      if (isHeader1Updatable && isHeader2Updatable) {
        return true;
      }

      if (isHeader1Updatable && !hasHeader2) {
        return true;
      }

      if (isHeader1Updatable && hasHeader2) {
        if (!sourceRow) return true;
        const sourceSubHeaderValue = sourceRow.parentHeaderId ? sourceRow.headerId : null;
        const targetSubHeaderValue = targetRow.original.parentHeaderId
          ? targetRow.original.headerId
          : null;
        return targetSubHeaderValue === null || sourceSubHeaderValue === targetSubHeaderValue;
      }

      if (!isHeader1Updatable && isHeader2Updatable) {
        if (!sourceRow) return true;
        const sourceParentHeaderValue = sourceRow.parentHeaderId ?? sourceRow.headerId;
        const targetParentHeaderValue =
          targetRow.original.parentHeaderId ?? targetRow.original.headerId;
        return sourceParentHeaderValue === targetParentHeaderValue;
      }

      return false;
    },
    [canDragBetweenHeaders, canDragDates, tableRows, header1FieldId, header2FieldId],
  );

  const rowGroupMetadata = useMemo(() => {
    if (!hasVerticalSubHeaders) return null;

    const groups: Array<{ firstRowIndex: number; lastRowIndex: number }> = [];

    for (let i = 0; i < tableRows.length; i++) {
      const row = tableRows[i];
      if (row?.original.isFirstSubRow) {
        const subRowCount = row.original.subRowCount ?? 1;
        groups.push({
          firstRowIndex: i,
          lastRowIndex: i + subRowCount - 1,
        });
      }
    }

    return groups;
  }, [tableRows, hasVerticalSubHeaders]);

  const rowRangeExtractor = useMemo(() => {
    if (!hasVerticalSubHeaders || !rowGroupMetadata) {
      return undefined;
    }

    return (range: { startIndex: number; endIndex: number }) => {
      const { startIndex, endIndex } = range;
      const indicesToInclude = new Set<number>();

      for (let i = startIndex; i <= endIndex; i++) {
        indicesToInclude.add(i);
      }

      for (const group of rowGroupMetadata) {
        if (group.lastRowIndex >= startIndex && group.firstRowIndex <= endIndex) {
          for (let i = group.firstRowIndex; i <= group.lastRowIndex; i++) {
            indicesToInclude.add(i);
          }
        }
      }

      return Array.from(indicesToInclude).sort((a, b) => a - b);
    };
  }, [hasVerticalSubHeaders, rowGroupMetadata]);

  const laneHeight = layout?.theme === 'compact' ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
  const rowGap = layout?.theme === 'compact' ? 2 : 0;
  const estimatedRowHeight = laneHeight + rowGap;

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: BASE_ROW_OVERSCAN,
    ...(rowRangeExtractor && { rangeExtractor: rowRangeExtractor }),
  });

  // Drag event handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'timeline-item') {
      const { color, name, itemId, duration, startDate, endDate, isCompact, currentRow } =
        active.data.current;
      setActiveDragData({
        color,
        name,
        itemId,
        duration,
        startDate,
        endDate,
        isCompact,
        currentRow,
      });
    }
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over, active } = event;
      const targetRowId = over?.id as string | null;
      const sourceRow = active?.data?.current?.currentRow as TimelineRowType | undefined;
      if (targetRowId && !isDropAllowed(sourceRow, targetRowId)) {
        setHoveredRowId(null);
        setForbiddenRowId(targetRowId);
        return;
      }
      setHoveredRowId(targetRowId);
      setForbiddenRowId(null);
    },
    [isDropAllowed],
  );

  const handleLaneDrop = useCallback(
    async (itemId: number, rowId: string, targetLaneIndex: number) => {
      const row = tableRows.find((r) => r.id === rowId);
      if (!row) return;

      const allItems = row.original.allItems ?? [];
      const sortedByOrder = [...allItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const otherItems = sortedByOrder.filter((item) => item.id !== itemId);
      if (otherItems.length === 0) return;

      const clampedIndex = Math.max(0, Math.min(targetLaneIndex, otherItems.length));

      const previousItem = clampedIndex > 0 ? otherItems[clampedIndex - 1] : undefined;
      const nextItem = clampedIndex < otherItems.length ? otherItems[clampedIndex] : undefined;

      const hasUnrankedAdjacent =
        (previousItem !== undefined && !itemRanks?.has(previousItem.id)) ||
        (nextItem !== undefined && !itemRanks?.has(nextItem.id));

      if (hasUnrankedAdjacent) {
        await VisualizationRanksApi.initializeRanks(
          viewId,
          visualizationId,
          sortedByOrder.map((item) => item.id),
        );
      }

      await VisualizationRanksApi.updateItemRank(viewId, visualizationId, itemId, {
        previousItemId: previousItem?.id,
        nextItemId: nextItem?.id,
      });

      queryClient.invalidateQueries({
        queryKey: loopVisualizationQueryKeys.itemRanks.detail(viewId, visualizationId),
      });
    },
    [tableRows, viewId, visualizationId, queryClient, itemRanks],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta, over } = event;
      if (active.data.current?.type !== 'timeline-item' || !delta) {
        setActiveDragData(null);
        setForbiddenRowId(null);
        return;
      }

      const { startDate, endDate } = active.data.current;
      const sourceRow = active?.data?.current?.currentRow as TimelineRowType | undefined;
      const daysOffset = Math.round(delta.x / dayWidth);
      const newStartDate = addDays(new Date(startDate), daysOffset);
      const newEndDate = addDays(new Date(endDate), daysOffset);

      const rect = active.rect.current.translated;
      if (!rect || !scrollContainerRef.current) {
        setActiveDragData(null);
        setForbiddenRowId(null);
        return;
      }

      if (over?.data?.current?.dataIndex !== undefined) {
        const targetRowId = over.id as string;
        if (!isDropAllowed(sourceRow, targetRowId)) {
          setActiveDragData(null);
          setHoveredRowId(null);
          setForbiddenRowId(null);
          return;
        }
        const newRow = tableRows[over.data.current.dataIndex];
        if (newRow && onItemDrop) {
          onItemDrop(
            active.data.current.itemId,
            newRow.original,
            format(newStartDate, 'yyyy-MM-dd'),
            format(newEndDate, 'yyyy-MM-dd'),
            sourceRow,
          );
        }

        if (newRow) {
          const currentOrder = active.data.current.order ?? 0;
          const laneOffset = Math.round(delta.y / laneHeight);
          const targetLane = Math.max(0, currentOrder + laneOffset);
          void handleLaneDrop(active.data.current.itemId, targetRowId, targetLane).catch(() => {});
        }
      }

      setActiveDragData(null);
      setHoveredRowId(null);
      setForbiddenRowId(null);
    },
    [
      onItemDrop,
      dayWidth,
      data,
      scrollContainerRef,
      isDropAllowed,
      handleLaneDrop,
      laneHeight,
      tableRows,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragData(null);
    setHoveredRowId(null);
    setForbiddenRowId(null);
  }, []);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualRows = isExportMode
    ? tableRows.map((_, index) => ({
        index,
        key: index,
        start: index * estimatedRowHeight,
        size: estimatedRowHeight,
      }))
    : virtualItems;
  const headerPadding = 6;
  const headerHeight = HEADER_CELL_HEIGHT * table.getHeaderGroups().length + headerPadding;
  const totalRowSize = rowVirtualizer.getTotalSize() + headerHeight;
  const virtualRowStart = isExportMode ? 0 : (virtualItems[0]?.start ?? 0);
  const tableHeight = isExportMode
    ? totalRowSize
    : virtualItems.length === 0
      ? Math.max(totalRowSize, 300)
      : totalRowSize;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      collisionDetection={pointerWithin}
      autoScroll={{ threshold: { x: 0, y: 0.2 } }}
    >
      <div
        className={cn('min-h-0 overflow-x-hidden', !isExportMode && 'overflow-y-auto')}
        ref={scrollContainerRef}
      >
        {onRowHeaderWidthChange && (
          <TimelineRowHeaderResize
            rowHeaderWidth={rowHeaderWidth}
            onRowHeaderWidthChange={onRowHeaderWidthChange}
            hasVerticalSubHeaders={hasVerticalSubHeaders}
            isVertical={isVertical}
            canEdit={canEdit}
            isPublishedMode={isPublishedMode}
            scrollContainerRef={scrollContainerRef}
          />
        )}
        <div className="flex relative pr-2" style={{ height: tableHeight }}>
          <TimelineRowAxisLabel
            scrollContainerRef={scrollContainerRef}
            headerHeight={headerHeight}
            layout={layout}
          />
          <div
            className="relative w-max"
            data-testid="timeline-container"
            ref={timelineContainerRefCallback}
          >
            {/* Header groups */}
            <div
              className={cn('w-max flex z-5 bg-bg-surface-base', !isExportMode && 'sticky top-0')}
            >
              <div
                className={cn(
                  'bg-bg-surface-base flex flex-col mb-1 rounded-tl-2xl rounded-tr-2xl border shadow-lg',
                  !isExportMode && 'overflow-hidden',
                )}
                data-export="column-header-container"
              >
                {table.getHeaderGroups().map((headerGroup, headerGroupIndex) => (
                  <div key={headerGroup.id} className="flex relative w-full">
                    {headerGroup.headers.map((header, headerIndex) => {
                      const width = header.getSize();
                      return (
                        <div
                          data-testid={`header-${header.id}`}
                          key={header.id}
                          className={cn('flex-1', {
                            'sticky bg-bg-surface-base z-10':
                              !isExportMode &&
                              ((headerGroupIndex === 0 && headerIndex === 0) ||
                                (headerGroupIndex === 1 && headerIndex < numFixedColumns)),
                          })}
                          style={{
                            minWidth: width,
                            width: width,
                            height: HEADER_CELL_HEIGHT,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Body rows */}
            <div
              className="flex flex-col relative h-max"
              style={{
                transform: `translateY(${virtualRowStart}px)`,
                overflow: 'visible',
              }}
            >
              {virtualRows.map((row) => {
                const rowCells = tableRows[row.index]?.getVisibleCells() || [];
                if (rowCells.length === 0) {
                  return null;
                }
                const originalRow = tableRows[row.index];
                const rowId = originalRow?.id || String(row.index);
                const isParent =
                  originalRow?.subRows && originalRow?.subRows.length > 0 && !hasVerticalSubHeaders;
                const sourceHeaderId = activeDragData?.currentRow?.headerId;
                const isDragTarget =
                  hoveredRowId === rowId ||
                  (sourceHeaderId !== undefined &&
                    originalRow?.original.headerId === sourceHeaderId);
                const allItems = originalRow?.original.allItems ?? [];
                const otherItemCount = activeDragData
                  ? allItems.filter((item) => item.id !== activeDragData.itemId).length
                  : 0;
                const extraLaneHeight =
                  activeDragData && isDragTarget && otherItemCount > 0 ? laneHeight : 0;
                const baseMinHeight = isParent ? PARENT_HEADER_HEIGHT : laneHeight;
                const minHeight = baseMinHeight + extraLaneHeight;
                const baseMaxHeight =
                  originalRow?.original.calculatedHeight ??
                  (originalRow?.original.maxOrder !== undefined
                    ? (originalRow.original.maxOrder + 1) * laneHeight
                    : undefined);
                const maxHeight =
                  baseMaxHeight !== undefined ? baseMaxHeight + extraLaneHeight : undefined;
                const isFirstSubRowOfGroup =
                  hasVerticalSubHeaders && originalRow?.original.isFirstSubRow;
                const fixedColumns = rowCells.slice(0, numFixedColumns);
                const visibleDateCells = rowCells.slice(numFixedColumns);

                return (
                  <TimelineDroppableRow
                    key={row.key}
                    isParent={Boolean(isParent)}
                    rowId={rowId}
                    className="flex relative w-full"
                    style={{
                      paddingBottom: rowGap,
                      overflowY: isFirstSubRowOfGroup ? 'visible' : undefined,
                    }}
                    measureElement={rowVirtualizer.measureElement}
                    dataIndex={row.index}
                  >
                    {fixedColumns.map((fixedColumn, colIndex) => {
                      const width = fixedColumn.column.getSize();
                      const isMainHeaderColumn = colIndex === 0 && hasVerticalSubHeaders;
                      const isFirstSubRowCell =
                        isMainHeaderColumn && originalRow?.original.isFirstSubRow;

                      return (
                        <div
                          key={fixedColumn.id}
                          className={cn(
                            'flex relative transition-all duration-10 ease-in-out bg-bg-surface-base',
                            {
                              'py-0.5': layout?.theme !== 'compact',
                              'z-10': isFirstSubRowCell,
                              'z-5': !isFirstSubRowCell,
                            },
                          )}
                          style={{
                            minWidth: width,
                            width,
                            minHeight,
                            maxHeight,
                            overflow: isFirstSubRowCell ? 'visible' : undefined,
                          }}
                          data-index={colIndex}
                        >
                          {flexRender(fixedColumn.column.columnDef.cell, fixedColumn.getContext())}
                        </div>
                      );
                    })}

                    {visibleDateCells.map((cell) => {
                      if (!cell) {
                        return null;
                      }
                      const width = cell.column.getSize();
                      return (
                        <div
                          key={cell.id}
                          className="flex bg-bg-surface-base"
                          style={{ minWidth: width, width: width, minHeight, maxHeight }}
                        >
                          {flexRender<TimelineTableCellContext<TimelineRowType, unknown>>(
                            cell.column.columnDef.cell,
                            {
                              ...cell.getContext(),
                              isRowDraggedOver: rowId === hoveredRowId,
                              isDropForbidden: rowId === forbiddenRowId,
                              extraLanes: extraLaneHeight > 0 ? 1 : 0,
                            },
                          )}
                        </div>
                      );
                    })}
                  </TimelineDroppableRow>
                );
              })}
            </div>

            {/* Dependency visualization overlay */}
            <DependencyLayer />
          </div>
        </div>
      </div>

      {/* Drag overlay */}
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {activeDragData && (
            <TimelineItemDragOverlayContent
              name={activeDragData.name}
              color={activeDragData.color}
              startDate={activeDragData.startDate}
              endDate={activeDragData.endDate}
              dayWidth={dayWidth}
              isCompact={layout?.theme === 'compact'}
            />
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
